import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { MagneticButton } from "./Motion";
import { Waveform, StaticWaveform } from "./Waveform";
import { Squiggle, ScribbleCircle, Blob, StarBurst, DashedArc, Sparkle, BnGlyph, SoundDoodle, DotGrid } from "./Decorations";
import { Play, Pause } from "lucide-react";
import { scrollToId } from "./useLenis";
import { API_URL } from "@/lib/api";
import { toast } from "sonner";

const HOMEPAGE_SAMPLE_URL = `/demos/demo_noor.wav?v=2`;

const Typewriter = ({ words }) => {
    const [index, setIndex] = useState(0);
    const [subIndex, setSubIndex] = useState(0);
    const [reverse, setReverse] = useState(false);
    const [blink, setBlink] = useState(true);

    useEffect(() => {
        const blinkTimeout = setTimeout(() => setBlink((prev) => !prev), 500);
        return () => clearTimeout(blinkTimeout);
    }, [blink]);

    useEffect(() => {
        if (index >= words.length) return;
        
        if (subIndex === words[index].length + 1 && !reverse) {
            const timeout = setTimeout(() => setReverse(true), 2500);
            return () => clearTimeout(timeout);
        }

        if (subIndex === 0 && reverse) {
            setReverse(false);
            setIndex((prev) => (prev + 1) % words.length);
            return;
        }

        const timeout = setTimeout(() => {
            setSubIndex((prev) => prev + (reverse ? -1 : 1));
        }, reverse ? 75 : 150);

        return () => clearTimeout(timeout);
    }, [subIndex, index, reverse, words]);

    if (index >= words.length) return null;
    
    return (
        <span className="relative inline-flex items-center">
            <span>{words[index].substring(0, subIndex)}</span>
            <span className={`inline-block w-[6px] h-[0.85em] bg-coral ml-[4px] align-baseline transition-opacity duration-100 ${blink ? "opacity-100" : "opacity-0"}`}></span>
        </span>
    );
};

export const Hero = () => {
    const headlineRef = useRef(null);
    const subRef = useRef(null);
    const ctaRef = useRef(null);
    const decorRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const ctx = gsap.context(() => {
            const chars = headlineRef.current?.querySelectorAll("[data-char]");
            if (chars) {
                gsap.set(chars, { yPercent: 110, opacity: 0, filter: "blur(8px)" });
                gsap.to(chars, {
                    yPercent: 0, opacity: 1, filter: "blur(0px)",
                    duration: 1.1, ease: "power3.out", stagger: 0.018, delay: 0.15,
                });
            }
            gsap.from(subRef.current, { y: 24, opacity: 0, duration: 0.9, ease: "power3.out", delay: 0.6 });
            gsap.from(ctaRef.current, { scale: 0.8, opacity: 0, duration: 0.8, ease: "back.out(1.6)", delay: 0.85 });
            const blobs = decorRef.current?.querySelectorAll("[data-blob]");
            if (blobs) {
                blobs.forEach((b, i) => {
                    gsap.from(b, {
                        x: i % 2 === 0 ? -120 : 120, y: i % 3 === 0 ? -60 : 40,
                        opacity: 0, rotate: i % 2 === 0 ? -20 : 20,
                        duration: 1.4, ease: "power3.out", delay: 0.2 + i * 0.06,
                    });
                });
            }
        });
        return () => ctx.revert();
    }, []);

    useEffect(() => {
        return () => {
            audioRef.current?.pause();
            audioRef.current = null;
        };
    }, []);

    const handleSampleToggle = async () => {
        if (playing) {
            audioRef.current?.pause();
            setPlaying(false);
            return;
        }
        if (!audioRef.current) {
            const audio = new Audio(HOMEPAGE_SAMPLE_URL);
            audioRef.current = audio;
            audio.onended = () => setPlaying(false);
            audio.onerror = () => {
                setPlaying(false);
                audioRef.current = null;
                toast.error("Could not load sample audio.");
            };
        }
        try {
            await audioRef.current.play();
            setPlaying(true);
        } catch {
            toast.error("Playback failed. Please try again.");
        }
    };

    const renderChars = (text, italic = false) => {
        return text.split("").map((c, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom" style={{ verticalAlign: "baseline" }}>
                <span data-char className={`inline-block will-change-transform ${italic ? "italic-display" : ""}`}>
                    {c === " " ? "\u00A0" : c}
                </span>
            </span>
        ));
    };

    return (
        <section id="top" className="relative pt-32 sm:pt-40 lg:pt-44 pb-20 lg:pb-32 overflow-hidden">
            {/* Decorative floating layer */}
            <div ref={decorRef} aria-hidden="true" className="absolute inset-0 pointer-events-none">
                <ScribbleCircle data-blob color="#FFD23F" className="absolute top-[8%] right-[8%] w-24 h-24 lg:w-32 lg:h-32 animate-float-slow"/>
                <Blob data-blob color="#FF4FA3" className="absolute top-[18%] left-[4%] w-32 h-32 lg:w-44 lg:h-44 opacity-90 animate-float-fast"/>
                <Squiggle data-blob color="#3D5BFF" className="absolute top-[42%] left-[10%] w-32 lg:w-44 animate-float-slow"/>
                <DashedArc data-blob color="#7B3FE4" className="absolute bottom-[14%] right-[14%] w-28 h-28 animate-spin-slow"/>
                <StarBurst data-blob color="#2EE8B5" className="absolute top-[12%] left-[42%] w-12 h-12 animate-float-fast"/>
                <Sparkle data-blob color="#FF5B3E" className="absolute bottom-[28%] left-[8%] w-10 h-10 animate-float-slow"/>
                <DotGrid data-blob color="#0A0A0A" rows={5} cols={5} className="absolute top-[58%] right-[5%] w-20 h-20 opacity-30"/>
                <BnGlyph data-blob glyph="অ" color="#3D5BFF" className="absolute -top-2 right-[28%] text-[180px] lg:text-[280px] leading-none opacity-[0.06] font-bold select-none"/>
                <BnGlyph data-blob glyph="ক" color="#FF5B3E" className="absolute bottom-0 left-[40%] text-[140px] lg:text-[220px] leading-none opacity-[0.05] font-bold select-none"/>
                <SoundDoodle data-blob color="#FF5B3E" className="absolute top-[52%] right-[2%] w-32 lg:w-44 animate-float-slow"/>
            </div>

            <div className="relative mx-auto max-w-[1240px] px-5 sm:px-8">
                {/* Live pill */}
                <div className="flex justify-center mb-7">
                    <span data-testid="hero-live-pill" className="inline-flex items-center gap-2 bg-white border border-cream-300 rounded-full px-4 py-1.5 text-[12.5px] font-medium text-ink shadow-soft">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-mint"></span>
                        </span>
                        Live for global publishers
                    </span>
                </div>

                {/* Massive headline */}
                <h1
                    ref={headlineRef}
                    data-testid="hero-headline"
                    className="h-display text-center text-[56px] sm:text-[88px] lg:text-[124px] xl:text-[140px] text-ink"
                    aria-label="Your articles, now in beautiful AI audio."
                >
                    <span className="block">{renderChars("Your articles,")}</span>
                    <span className="block">{renderChars("now in ")}<span>{renderChars("beautiful", true)}</span></span>
                    <span className="block">
                        <span className="inline-block overflow-hidden align-bottom">
                            <span data-char className="inline-block will-change-transform bn font-display font-medium text-coral min-w-[200px] text-left">
                                <Typewriter words={["বাংলা", "English", "عربى", "Español", "Français"]} />
                            </span>
                        </span>
                        {renderChars(" audio.")}
                    </span>
                </h1>

                <p
                    ref={subRef}
                    data-testid="hero-sub"
                    className="mx-auto mt-9 max-w-[640px] text-center text-[17px] sm:text-[19px] leading-[1.55] text-ink-muted font-normal"
                >
                    Turn any Bengali news article into natural-sounding audio in <span className="font-semibold text-ink">30 seconds</span>. Embed it anywhere with one line of code. Built for the <span className="font-semibold text-ink">300M</span> who'd rather listen.
                </p>

                <div ref={ctaRef} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <MagneticButton
                        data-testid="hero-cta-primary"
                        onClick={() => navigate("/register")}
                        className="group bg-ink text-cream-100 rounded-full px-7 py-4 text-[15px] font-semibold shadow-pill hover:shadow-soft-lg transition-shadow inline-flex items-center gap-2"
                    >
                        Start Free
                        <span className="transition-transform group-hover:translate-x-1">→</span>
                    </MagneticButton>
                    <button
                        data-testid="hero-cta-secondary"
                        onClick={handleSampleToggle}
                        className="text-ink hover:text-coral transition-colors text-[15px] font-medium inline-flex items-center gap-2 underline-offset-4 hover:underline decoration-2 decoration-coral"
                    >
                        Hear a sample <span aria-hidden="true">▸</span>
                    </button>
                </div>

                <p data-testid="hero-microcopy" className="mt-4 text-center text-[13px] text-ink-muted">
                    No credit card · 20 free articles/month
                </p>

                {/* Animated waveform signature */}
                <div className="mt-14 lg:mt-20 flex justify-center">
                    <div className="w-full max-w-[820px] h-[100px] lg:h-[140px] opacity-90">
                        <Waveform bars={56} color="#FF5B3E" height={120} playing={playing}/>
                    </div>
                </div>

                {/* Floating audio player card */}
                <div data-testid="hero-mini-player" className="mt-12 mx-auto max-w-[460px] bg-white rounded-3xl p-5 shadow-soft-lg border border-cream-300 relative">
                    <div className="absolute -top-3 -left-3">
                        <Sparkle color="#FFD23F" className="w-7 h-7"/>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-coral animate-pulse"/>
                        <span className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold">Now playing — sample</span>
                    </div>
                    <p className="bn text-[15px] leading-[1.5] text-ink mb-4">
                        বাংলাদেশের অর্থনীতি ২০২৬ সালে নতুন উচ্চতায় পৌঁছেছে। গত মাসে রপ্তানি আয় বেড়েছে ১২ শতাংশ।
                    </p>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleSampleToggle}
                            data-testid="hero-mini-play"
                            className="w-11 h-11 rounded-full bg-ink text-cream-100 flex items-center justify-center hover:bg-coral hover:scale-105 transition-all flex-shrink-0"
                            aria-label={playing ? "Pause" : "Play"}
                        >
                            {playing ? <Pause size={16} fill="currentColor"/> : <Play size={16} className="ml-0.5" fill="currentColor"/>}
                        </button>
                        <div className="flex-1 h-8 flex items-center">
                            <StaticWaveform bars={32} color={playing ? "#FF5B3E" : "#0A0A0A"} height={32}/>
                        </div>
                        <span className="font-mono text-[11px] text-ink-muted">0:42</span>
                    </div>
                </div>
            </div>
        </section>
    );
};
